<script>
import AdminLayout from '@layouts/admin-main'
import PrivateMarketInfo from '@components/admin/private-market-info'
import DirectMarketInfo from '@components/admin/direct-market-info'
import PublicMarketInfo from '@components/admin/public-market-info'
import {
  fetchPrivateMarkets,
  fetchDirectMarkets,
  fetchPublicMarkets,
} from '@apis/admin/market'

export default {
  page: {
    title: 'Admin::Market',
    meta: [{ name: 'description', content: 'Admin::Market' }],
  },
  components: {
    AdminLayout,
    PrivateMarketInfo,
    DirectMarketInfo,
    PublicMarketInfo,
  },
  data() {
    return {
      private_markets: [],
      direct_markets: [],
      public_markets: [],
      target_private_market_seq: 0,
      show_private_market_info: false,
      target_direct_market_seq: 0,
      show_direct_market_info: false,
      target_public_market_seq: 0,
      show_public_market_info: false,
    }
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      Promise.all([
        this.loadPrivateMarkets(),
        this.loadDirectMarkets(),
        this.loadPublicMarkets(),
      ]).catch((e) => this.$toasted.error(e.message))
    },
    async loadPrivateMarkets() {
      const { data } = await fetchPrivateMarkets()

      this.private_markets = data
    },
    async loadDirectMarkets() {
      const { data } = await fetchDirectMarkets()

      this.direct_markets = data
    },
    async loadPublicMarkets() {
      const { data } = await fetchPublicMarkets()

      this.public_markets = data
    },
    handleCreatePrivateMarket() {
      this.show_private_market_info = true
      this.target_private_market_seq = null
    },
    handleUpdatePrivateMarket({ seq }) {
      this.show_private_market_info = true
      this.target_private_market_seq = seq
    },
    handlePrivateMarketSubmit(seq) {
      this.init()
      this.target_private_market_seq = seq
    },
    handleCreateDirectMarket() {
      this.show_direct_market_info = true
      this.target_direct_market_seq = null
    },
    handleUpdateDirectMarket({ seq }) {
      this.show_direct_market_info = true
      this.target_direct_market_seq = seq
    },
    handleDirectMarketSubmit(seq) {
      this.init()
      this.target_direct_market_seq = seq
    },
    handleCreatePublicMarket() {
      this.show_public_market_info = true
      this.target_public_market_seq = null
    },
    handleUpdatePublicMarket({ seq }) {
      this.show_public_market_info = true
      this.target_public_market_seq = seq
    },
    handlePublicMarketSubmit(seq) {
      this.init()
      this.target_public_market_seq = seq
    },
  },
}
</script>

<template>
  <AdminLayout>
    <div>
      <h3 :class="$style.xeAdmin">{{ $t('admin.c2c_market') }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.colTable">
        <thead>
          <tr>
            <th scope="col" rowspan="2"
              ><div>{{ $t('admin.market_title') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.source_asset_title') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.market_source_min_amount') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.deal_min_price') }}</div></th
            >
            <th scope="col"><div>{{ $t('market.fee_rate') }}</div></th>
            <th scope="col" rowspan="2"
              ><div>{{ $t('admin.market_source_amount_unit') }}</div></th
            >
            <th scope="col" rowspan="2"
              ><div>{{ $t('admin.is_active') }}</div></th
            >
            <th scope="col" rowspan="2">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.button, $style.black]"
                  @click.prevent="handleCreatePrivateMarket"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.target_asset_title') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.market_source_max_amount') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.deal_max_price') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('market.requester_fee_rate') }}</div></th
            >
          </tr>
        </thead>
        <tbody>
          <template
            v-for="(market, idx) in private_markets"
            :class="$style[`bg${idx % 2}`]"
          >
            <tr :key="`${market.seq}-1`">
              <td rowspan="2" :class="[$style.tCenter, $style.wide]">{{
                market.title
              }}</td>
              <td :class="[$style.tCenter, $style.nowrap]">{{
                market.source_asset_title
              }}</td>
              <td :class="[$style.tRight, $style.nowrap]">{{
                market.use_source_min_amount === 'Y'
                  ? market.source_min_amount
                  : $t('admin.more_zero')
              }}</td>
              <td :class="[$style.tRight, $style.nowrap]">{{
                market.use_min_price === 'Y'
                  ? market.min_price
                  : $t('admin.more_zero')
              }}</td>
              <td :class="[$style.tRight, $style.nowrap]"
                >{{
                  market.deal_fee_rate | trimDecimalZero | numberFormat
                }}
                %</td
              >
              <td rowspan="2" :class="[$style.tRight, $style.nowrap]">{{
                market.use_source_amount_unit === 'Y'
                  ? market.source_amount_unit
                  : $t('admin.no_use_cover')
              }}</td>
              <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
                market.is_active
              }}</td>
              <td rowspan="2" :class="[$style.tRight, $style.nowrap]">
                <a
                  href="#"
                  :class="[$style.button]"
                  @click.prevent="handleUpdatePrivateMarket(market)"
                  ><span>{{ $t('admin.modify') }}</span></a
                >
              </td>
            </tr>
            <tr :key="`${market.seq}-2`">
              <td :class="[$style.tCenter, $style.nowrap]">{{
                market.target_asset_title
              }}</td>
              <td :class="[$style.tRight, $style.nowrap]">{{
                market.use_source_max_amount === 'Y'
                  ? market.source_max_amount
                  : $t('admin.market_no_limit')
              }}</td>
              <td :class="[$style.tRight, $style.nowrap]">{{
                market.use_max_price === 'Y'
                  ? market.max_price
                  : $t('admin.market_no_limit')
              }}</td>
              <td :class="[$style.tRight, $style.nowrap]"
                >{{ market.fee_rate | trimDecimalZero | numberFormat }} %</td
              >
            </tr>
          </template>
          <tr v-if="!private_markets.length">
            <td :colspan="8" :class="$style.tCenter">{{
              $t('admin.no_c2c_market')
            }}</td>
          </tr>
        </tbody>
      </table>
      <h3 :class="$style.xeAdmin">{{ $t('admin.direct_transfer_market') }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.colTable">
        <thead>
          <tr>
            <th scope="col" rowspan="2"
              ><div>{{ $t('admin.transfer_asset_pair') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.transfer_source_asset_title') }}</div></th
            >
            <th scope="col" rowspan="2"
              ><div>{{ $t('admin.direct_basic_rate') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.direct_bid_spread_rate') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.direct_allow_bid') }}</div></th
            >
            <th scope="col" rowspan="2"
              ><div>{{ $t('admin.is_active') }}</div></th
            >
            <th scope="col" rowspan="2">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.button, $style.black]"
                  @click.prevent="handleCreateDirectMarket"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.transfer_target_asset_title') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.direct_ask_spread_rate') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.direct_allow_ask') }}</div></th
            >
          </tr>
        </thead>
        <tbody>
          <template
            v-for="(market, idx) in direct_markets"
            :class="$style[`bg${idx % 2}`]"
          >
            <tr :key="`${market.seq}-1`">
              <td rowspan="2" :class="[$style.tCenter]"
                >{{ market.source_asset_code }}/{{
                  market.target_asset_code
                }}</td
              >
              <td :class="[$style.tCenter, $style.nowrap]">{{
                market.source_asset_title
              }}</td>
              <td rowspan="2" :class="[$style.tRight, $style.nowrap]"
                >{{ market.source_asset_code }} = {{ market.basic_rate }}
                {{ market.target_asset_code }}</td
              >
              <td :class="[$style.tRight, $style.nowrap]"
                >{{ market.bid_spread_rate }}%</td
              >
              <td :class="[$style.tCenter, $style.nowrap]">{{
                market.allow_bid
              }}</td>
              <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
                market.is_active
              }}</td>
              <td rowspan="2" :class="[$style.tRight, $style.nowrap]">
                <a
                  href="#"
                  :class="[$style.button]"
                  @click.prevent="handleUpdateDirectMarket(market)"
                  ><span>{{ $t('admin.modify') }}</span></a
                >
              </td>
            </tr>
            <tr :key="`${market.seq}-2`">
              <td :class="[$style.tCenter, $style.nowrap]">{{
                market.target_asset_title
              }}</td>
              <td :class="[$style.tRight, $style.nowrap]"
                >{{ market.ask_spread_rate }}%</td
              >
              <td :class="[$style.tCenter, $style.nowrap]">{{
                market.allow_ask
              }}</td>
            </tr>
          </template>
          <tr v-if="!direct_markets.length">
            <td :colspan="8" :class="$style.tCenter">{{
              $t('admin.no_direct_market')
            }}</td>
          </tr>
        </tbody>
      </table>
      <h3 :class="$style.xeAdmin">{{ $t('admin.b2c_market') }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.colTable">
        <thead>
          <tr>
            <th scope="col" rowspan="2"
              ><div>{{ $t('market.market_name') }}</div></th
            >
            <th scope="col" rowspan="2"
              ><div>{{ $t('market.market_code') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('market.base_assets') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('market.quantity_constructor_commission') }}</div></th
            >
            <th scope="col" rowspan="2"
              ><div>{{ $t('admin.is_active') }}</div></th
            >
            <th scope="col" rowspan="2">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.button, $style.black]"
                  @click.prevent="handleCreatePublicMarket"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('market.relative_assets') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('market.quantity_user_fee_rate') }}</div></th
            >
          </tr>
        </thead>
        <tbody>
          <template
            v-for="(market, idx) in public_markets"
            :class="$style[`bg${idx % 2}`]"
          >
            <tr :key="`${market.seq}-1`">
              <td rowspan="2" :class="[$style.tCenter]">{{ market.title }}</td>
              <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
                market.code
              }}</td>
              <td :class="[$style.tCenter, $style.nowrap]">{{
                market.source_asset_title
              }}</td>
              <td :class="[$style.tRight, $style.nowrap]"
                >{{
                  market.maker_fee_rate | trimDecimalZero | numberFormat
                }}
                %</td
              >
              <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
                market.is_active
              }}</td>
              <td rowspan="2" :class="[$style.tRight, $style.nowrap]">
                <a
                  href="#"
                  :class="[$style.button]"
                  @click.prevent="handleUpdatePublicMarket(market)"
                  ><span>{{ $t('admin.modify') }}</span></a
                >
              </td>
            </tr>
            <tr :key="`${market.seq}-2`">
              <td :class="[$style.tCenter, $style.nowrap]">{{
                market.target_asset_title
              }}</td>
              <td :class="[$style.tRight, $style.nowrap]"
                >{{
                  market.taker_fee_rate | trimDecimalZero | numberFormat
                }}
                %</td
              >
            </tr>
          </template>
          <tr v-if="!public_markets.length">
            <td :colspan="6" :class="$style.tCenter">{{
              $t('market.msg_no_registered')
            }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <ElDialog
      :title="
        target_private_market_seq
          ? $t('admin.c2c_market_modify')
          : $t('admin.c2c_market_enroll')
      "
      :visible.sync="show_private_market_info"
      width="1280px"
    >
      <PrivateMarketInfo
        v-if="show_private_market_info"
        :key="target_private_market_seq"
        :market_seq="target_private_market_seq"
        @submit="handlePrivateMarketSubmit"
      ></PrivateMarketInfo>
    </ElDialog>
    <ElDialog
      :title="
        target_direct_market_seq
          ? $t('admin.direct_market_modify')
          : $t('admin.direct_market_enroll')
      "
      :visible.sync="show_direct_market_info"
    >
      <DirectMarketInfo
        v-if="show_direct_market_info"
        :key="target_direct_market_seq"
        :market_seq="target_direct_market_seq"
        @submit="handleDirectMarketSubmit"
      ></DirectMarketInfo>
    </ElDialog>
    <ElDialog
      :title="
        target_public_market_seq
          ? $t('market.modification')
          : $t('market.registration')
      "
      :visible.sync="show_public_market_info"
    >
      <PublicMarketInfo
        v-if="show_public_market_info"
        :key="target_public_market_seq"
        :market_seq="target_public_market_seq"
        @submit="handlePublicMarketSubmit"
      ></PublicMarketInfo>
    </ElDialog>
  </AdminLayout>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
